import { useCallback, useMemo } from 'react';
import {
  CustomerData,
  getCustomerInvite,
  getWorkspaceId,
  useAppSelector,
  useLazyGetCustomerListQuery,
} from '@zonofi/zono-money-store';
import { useDebounceSearch, usePaginatedList } from '../../hooks';
import { onClickBackToWhatsapp } from '../../helper';

export const useSelectCustomerList = () => {
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const inviteDetails = useAppSelector(getCustomerInvite);
  const { seller, waConfig } = inviteDetails;
  const { debouncedSearchValue } = useDebounceSearch();

  const [getCustomerListApi, customerListApiResponse] =
    useLazyGetCustomerListQuery();

  const fetchCustomers = useCallback(
    (params: { pageNo: number; pageSize: number }) => {
      getCustomerListApi({
        workspaceId: sellerWorkspaceId,
        ...(debouncedSearchValue ? { searchKey: debouncedSearchValue } : {}),
        ...params,
      });
    },
    [debouncedSearchValue]
  );

  const memoizedCustomerData = useMemo(() => {
    return customerListApiResponse?.data?.customers ?? [];
  }, [customerListApiResponse?.data?.customers]);

  const {
    data: customerData,
    pageNo,
    lastItemRef,
    isPageLoading,
    onScroll,
    isEndOfList,
    refresh,
  } = usePaginatedList<CustomerData>({
    fetchDataApiCall: fetchCustomers,
    apiResponseData: memoizedCustomerData,
    isLoading: customerListApiResponse.isLoading,
    uniqueKey: 'id',
    dependencies: [debouncedSearchValue],
    isError: customerListApiResponse.isError,
    paginationMeta: {
      startRecord: customerListApiResponse?.data?.startRecord || 0,
      endRecord: customerListApiResponse?.data?.endRecord || 0,
      totalCount: customerListApiResponse?.data?.totalCount || 0,
    },
  });

  const onWhatsAppPress = () => {
    onClickBackToWhatsapp({
      whatsappPhoneNo: waConfig?.whatsappPhoneNo,
      workspaceId: seller?.workspaceId,
    });
  };

  return {
    customerData,
    lastItemRef,
    isPageLoading,
    onScroll,
    onWhatsAppPress,
    isloading:
      pageNo === 1 &&
      (customerListApiResponse?.isUninitialized ||
        customerListApiResponse?.isFetching),
    isEndOfList,
    refresh,
  };
};
